// SimCommand: the only way anything (player UI or AI) mutates the sim (plan §8.4).

import type { PlayerId } from '../content/types';
import type { Vec2 } from '../shared/vec';
import type { EntId, HLKind } from './state';

export type PlayerCommand =
  | { kind: 'PlayCard'; player: PlayerId; cardId: string; targetPos: Vec2; targetEntity?: EntId }
  | {
      kind: 'IssueCommand';
      player: PlayerId;
      squadIds: EntId[];
      command: HLKind;
      targetPos?: Vec2;
      targetEntity?: EntId;
    }
  | { kind: 'SetAllocation'; player: PlayerId; power: number }
  | { kind: 'SetAutoWorker'; player: PlayerId; enabled: boolean }
  | { kind: 'QueueWorker'; player: PlayerId }
  | { kind: 'QueueUnit'; player: PlayerId; buildingId: EntId; unitId: string }
  | { kind: 'CancelQueueItem'; player: PlayerId; buildingId: EntId; index: number }
  | { kind: 'ToggleAutoProduce'; player: PlayerId; buildingId: EntId; unitId: string }
  | { kind: 'RecallWorkers'; player: PlayerId; hubId?: EntId }
  | { kind: 'ResumeWork'; player: PlayerId; hubId?: EntId }
  | { kind: 'RepairOrder'; player: PlayerId; workerIds: EntId[]; buildingId: EntId }
  | { kind: 'WorkerRetreat'; player: PlayerId; workerIds: EntId[] }
  | { kind: 'SetRally'; player: PlayerId; buildingId: EntId; pos: Vec2 }
  | { kind: 'BuyUpgrade'; player: PlayerId; buildingId: EntId }
  | { kind: 'BuyCoreUpgrade'; player: PlayerId; buildingId: EntId; upgradeId: string }
  | { kind: 'QueueTowerWorker'; player: PlayerId; buildingId: EntId }
  | { kind: 'CancelConstruction'; player: PlayerId; buildingId: EntId }
  | { kind: 'ClaimRuin'; player: PlayerId; ruinId: EntId }
  | { kind: 'BuildBaseWall'; player: PlayerId; group: string }
  | { kind: 'MountWall'; player: PlayerId; squadIds: EntId[]; buildingId: EntId }
  | { kind: 'MountSquad'; player: PlayerId; squadIds: EntId[]; hostId: EntId }
  | { kind: 'Dismount'; player: PlayerId; squadIds: EntId[] }
  | { kind: 'Sacrifice'; player: PlayerId; squadId: EntId; receiverId?: EntId }
  | { kind: 'ToggleBuildingMode'; player: PlayerId; buildingId: EntId }
  | { kind: 'ToggleTowerWorker'; player: PlayerId; buildingId: EntId }
  // ---- Core Annex System (owner spec 2026-07-13) ----
  /** E: deploy a building-carrier (Seedback, Wagon, Hearse, Skiff) where it stands */
  | { kind: 'DeployCarrier'; player: PlayerId; squadId: EntId }
  /** E: Take Root / Uproot a rootable guardian (Rootwarden Treant) */
  | { kind: 'ToggleRoot'; player: PlayerId; squadId: EntId }
  /** Deeproot Sanctuary: burrow retreating workers into the Core shelter */
  | { kind: 'DeepRecall'; player: PlayerId }
  /** Conduit Nexus: phase sheltered workers to a connected conduit building */
  | { kind: 'TransferWorkers'; player: PlayerId; buildingId: EntId }
  /** Sanguine Crucible: toggle the Blood Tithe */
  | { kind: 'SetTithe'; player: PlayerId; active: boolean }
  /** Echo Observatory: arm/disarm automatic duplication of the next squad */
  | { kind: 'SetEchoArmed'; player: PlayerId; armed: boolean }
  /** Sanguine Crucible: choose the Apotheosis result */
  | { kind: 'ChooseApotheosis'; player: PlayerId; form: 'amalgam' | 'sovereign' };

export type DebugCommand =
  | { kind: 'CheatResources'; player: PlayerId; power: number; essence: number }
  | { kind: 'CheatAscension'; player: PlayerId }
  /** UAT staging: seed corpse marks around the Core (Ashen economy testing) */
  | { kind: 'CheatCorpse'; player: PlayerId; count: number }
  /** UAT staging: complete all of this player's channeling rituals instantly */
  | { kind: 'CheatFinishRituals'; player: PlayerId }
  /** Developer battle staging: populate the map with a large opening engagement. */
  | { kind: 'EpicBattle'; player: PlayerId };

export type SimCommand = PlayerCommand | DebugCommand;

export type PlayerCommandPayload = PlayerCommand extends infer Command
  ? Command extends { player: PlayerId }
    ? Omit<Command, 'player'>
    : never
  : never;

export interface CommandResult {
  status: 'applied' | 'denied';
  reason?: string;
}

export function isDebugCommand(command: SimCommand): command is DebugCommand {
  return command.kind === 'CheatResources' || command.kind === 'CheatAscension' || command.kind === 'CheatCorpse' || command.kind === 'CheatFinishRituals' || command.kind === 'EpicBattle';
}
